import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import * as _ from 'lodash';
import {
  Inspection,
  InspectionSection,
  InspectionPoint,
  InspectionPlugin,
  InspectionFolder,
  Icon,
  Icons,
  InspectionRestoreItem
} from '../models/';
import { PluginComponent } from '../../plugins/plugin.component';
import { TranslationService } from './translation.service';
import { EntityType, InspectionSession } from '../models/multi-edit.model';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  public inspectionsById: any = {};
  private changedPoints: any = {};

  constructor(private translationService: TranslationService) {}

  public getInspection(id: string) {
    const inspection = this.inspectionsById[id];
    return inspection;
  }

  public getInspections(): Observable<Inspection[]> {
    return of(_.values(this.inspectionsById).filter((el: Inspection) => !el.isTemplate()));
  }

  public getModels(): Observable<Inspection[]> {
    return of(_.values(this.inspectionsById).filter((el: Inspection) => el.isTemplate()));
  }

  public getPublishedModels(): Observable<Inspection[]> {
    return of(_.values(this.inspectionsById).filter((el: Inspection) => el.isTemplate() && el.status === 100));
  }

  public getSections(inspectionsId: string): Observable<InspectionSection[]> {
    const inspection = this.inspectionsById[inspectionsId];
    return of(inspection ? inspection.sections : []);
  }

  public getInspectionPoints(inspectionsId: string): Observable<InspectionPoint[]> {
    const inspection = this.inspectionsById[inspectionsId];
    return of(inspection ? inspection.points : []);
  }

  public getSectionPoints(inspectionsId: string, sectionId: string): Observable<InspectionPoint[]> {
    const inspection = this.inspectionsById[inspectionsId];
    if (inspection == null) return of(null);
    const section = inspection.sectionsById[sectionId];
    return of(section ? section.points : []);
  }

  public getSection(inspectionsId: string, sectionId: string): Observable<InspectionSection> {
    const inspection = this.inspectionsById[inspectionsId];
    if (inspection == null) return of(null);
    const section = inspection.sectionsById[sectionId];
    return of(section);
  }

  public getFolder(inspectionsId: string, folderId: string): Observable<InspectionFolder> {
    const inspection = this.inspectionsById[inspectionsId];
    if (inspection == null) return of(null);

    const folder = inspection.foldersById[folderId];
    return of(folder);
  }

  public getPoint(inspectionsId: string, pointId: string): Observable<InspectionPoint> {
    const inspection = this.inspectionsById[inspectionsId];
    if (inspection == null) return of(null);
    const point = inspection.pointsById[pointId];
    return of(point);
  }

  public getPlugin(inspectionsId: string, pluginId: string): Observable<InspectionPlugin> {
    const inspection = this.inspectionsById[inspectionsId];
    if (inspection == null) return of(null);
    const plugin = inspection.pluginsById[pluginId];
    return of(plugin);
  }

  public getInspectionLevelPlugin(inspectionsId: string, pluginType: string): Observable<InspectionPlugin> {
    const inspection = this.inspectionsById[inspectionsId] as Inspection;
    if (inspection == null) return of(null);
    const plugin = inspection.plugins.find((p) => p.pluginType === pluginType);
    return of(plugin);
  }

  public getParrentInspectionLevelPlugin(inspectionsId: string, pluginType: string): Observable<InspectionPlugin> {
    const inspection = this.inspectionsById[inspectionsId] as Inspection;
    if (inspection == null) return of(null);
    if (inspection.parentId == null) return of(null);
    const parentInspection = this.inspectionsById[inspection.parentId] as Inspection;
    if (parentInspection == null) return of(null);
    const plugin = parentInspection.plugins.find((p) => p.pluginType === pluginType);
    return of(plugin);
  }

  public getPointLevelPlugin(inspectionsId: string, pointId: string, pluginType: string): Observable<InspectionPlugin> {
    const inspection = this.inspectionsById[inspectionsId] as Inspection;
    if (inspection == null) return of(null);
    const plugin = inspection.points.find((x) => x.id === pointId).plugins.find((p) => p.pluginType === pluginType);
    return of(plugin);
  }

  public getPartsPlugin(inspectionsId: string): Observable<InspectionPlugin> {
    const inspection = this.inspectionsById[inspectionsId];
    if (inspection == null) return of(null);
    const plugin = inspection.parts;
    return of(plugin);
  }

  public removeParts(inspectionId: string, points: string[]) {
    const inspection = this.inspectionsById[inspectionId];
    if (inspection == null) {
      return null;
    }
    const plugin = inspection.parts;
    if (!plugin) {
      return null;
    }
    if (plugin.config.Parts) {
      plugin.config.Parts = plugin.config.Parts.filter((p) => {
        return !points.includes(p.PointId);
      });
      const p: InspectionPlugin = inspection.pluginsById[plugin.id];
      p.config = plugin.config;
    }
  }

  public updatePlugin(id: string, plugin: InspectionPlugin, resetToDraft = true): any {
    const current: Inspection = this.inspectionsById[id];

    if (current) {
      const p: InspectionPlugin = current.pluginsById[plugin.id];
      if (p) {
        if (plugin) {
          p.config = plugin.config;
          p.order = plugin.order;
        }
        if (p.point && resetToDraft) {
          p.point.status = InspectionPoint.Draft;
          const pointsCount = current.points.filter((p) => p.isDraft() || p.isDone()).length;
          const donePoints = current.points.filter((p) => p.isDone()).length;
          current.progression = Math.round((donePoints / pointsCount) * 100);
        }
      }
    }
  }

  public livePluginUpdate(id: string, plugin: InspectionPlugin) {
    this.addPlugin(id, [plugin]);
    this.updatePlugin(id, plugin);

    switch (plugin.pluginType) {
      case 'customerselector':
        this.updateCustomer(id, plugin.config);
        break;
      case 'machineselector':
        plugin.config.name = plugin.config.ModelName;
        this.updateInspectionMachine(id, plugin.config);
        break;
      case 'optimisationmachineselector':
        plugin.config.Machines?.forEach((m) => (m.name = m.ModelId));
        this.updateInspectionMachine(id, plugin.config.Machines);
        break;
      case 'installationbobstcontact':
      case 'contact':
        this.updateInspectionContact(id, plugin.config);
        break;
      case 'inspectioninfo':
      case 'installationinspectioninfo':
        this.updateInspectionDate(id, plugin.config);
        break;
    }
  }

  public updatePluginConfig(id: string, pluginId: string, config: any): any {
    const current: Inspection = this.inspectionsById[id];

    if (current) {
      const p: InspectionPlugin = current.pluginsById[pluginId];
      if (p) {
        if (config) {
          p.config = config;
        }
      }
    }
    return config;
  }

  public addPlugin(id: string, plugins: InspectionPlugin[]): any {
    const current: Inspection = this.inspectionsById[id];
    if (current) {
      for (let i = 0; i < plugins.length; i++) {
        const plugin = plugins[i];
        plugins[i] = this.processPlugin(current, _.merge(new InspectionPlugin(plugin.id, plugin.order, plugin.pluginType), plugin));
      }
    }
  }

  public updatePoint(id: string, point: InspectionPoint): InspectionPoint {
    const current: Inspection = this.inspectionsById[id];
    if (current) {
      const p: InspectionPoint = current.pointsById[point.id];
      point.plugins = p?.plugins;
      point.icons = p?.icons;
      point.sessions = p?.sessions;
      point = this.processPoint(current, point);
      _.merge(p, point);
      return p;
    }
  }

  public updatePoints(id: string, points: InspectionPoint[]): InspectionPoint[] {
    const results: InspectionPoint[] = [];
    for (let i = 0; i < points.length; i++) {
      results.push(this.updatePoint(id, points[i]));
    }
    return results;
  }

  public updateFolder(id: string, sectionId: string, folder: InspectionFolder): InspectionFolder {
    const current: Inspection = this.inspectionsById[id];

    if (current) {
      const section = current.sectionsById[sectionId];
      if (section) {
        folder = this.processFolder(
          current,
          section,
          _.merge(new InspectionFolder(folder.id, folder.name, folder.description), folder),
          section
        );
        const f = current.foldersById[folder.id];
        if (!section.folders) section.folders = [];
        section.folders.push(f);

        return f;
      }
    }
  }

  public updateSection(id: string, section: InspectionSection): InspectionSection {
    const current: Inspection = this.inspectionsById[id];
    if (current) {
      const s: InspectionSection = current.sectionsById[section.id];
      section.points = s?.points;
      section.icons = s?.icons;
      section.sessions = s?.sessions;
      section = this.processSection(current, section);
      _.merge(s, section);
      return s;
    }
  }

  public updateSectionPointsOrder(id: string, section: InspectionSection, orderableItems) {
    const current: Inspection = this.inspectionsById[id];
    const currentSection: InspectionSection = current.sectionsById[section.id];
    orderableItems.forEach((o) => {
      const orderableItem = currentSection.points.find((p) => o.id === p.id)
        ? currentSection.points.find((p) => o.id === p.id)
        : currentSection.folders.find((f) => o.id === f.id);
      orderableItem.order = o.order;
    });
    this.processSection(current, currentSection);
    return current.sectionsById[section.id];
  }

  public updatePointPluginsOrder(id: string, pointId: string, orderableItems) {
    const current: Inspection = this.inspectionsById[id];
    const currentPoint: InspectionPoint = current.pointsById[pointId];
    currentPoint.plugins.forEach((p) => {
      p.order = orderableItems.find((o) => o.id === p.id).order;
    });
  }

  public deleteFile(id: string, plugin: InspectionPlugin): any {
    const current: Inspection = this.inspectionsById[id];
    if (current) {
      const p: InspectionPlugin = current.pluginsById[plugin.id];
      _.merge(p, plugin);
      p.config = plugin.config;
      if (p.point) {
        p.point.status = InspectionPoint.Draft;
      }
    }
  }

  // processing
  public processInspections(inspections: Inspection[]): Inspection[] {
    for (let i = 0; i < inspections.length; i++) {
      const inspection = inspections[i];
      this.processInspection(inspection);
    }
    return _.values(this.inspectionsById);
  }

  public mergeInspection(inspection: Inspection) {
    this.processInspection(inspection);
  }

  public processInspection(inspection: Inspection): Inspection {
    let current: Inspection = this.inspectionsById[inspection.id];
    if (current == null) {
      this.inspectionsById[inspection.id] = _.merge(new Inspection(inspection.id, inspection.name, inspection.description), inspection);
    } else {
      this.inspectionsById[inspection.id] = _.assign(current, inspection);
    }

    current = this.inspectionsById[inspection.id];

    if (!('search' in current)) {
      const isLoaded = current['isLoaded'];
      current = _.merge(new Inspection(inspection['id'], inspection['name'], inspection['description']), current);
      current.isLoaded = isLoaded;
    }

    this.processSections(current, current.sections);
    this.processSectionItems(current, current.sectionsItems);
    this.processPoints(current, current.points);
    this.processPlugins(current, current.allPlugins);

    if ((<any>current).translations) {
      this.translationService.applyTranslations(current, (<any>current).translations, (<any>current).inputLanguage || 'en');
    }

    current.setDataProperties();
    return current;
  }

  public processSections(inspection: Inspection, sections: InspectionSection[]) {
    for (let i = 0; i < sections.length; i++) {
      sections[i].points = [];
      const section = sections[i];
      sections[i] = this.processSection(inspection, section);
    }
  }

  public processSectionItems(inspection: any, sectionsItems: any) {
    if (!inspection.sectionsById) {
      inspection.sectionsById = {};
    }
    if (sectionsItems) {
      sectionsItems.forEach((s) => {
        if (inspection.sectionsById[s.id]) {
          inspection.sectionsById[s.id].order = s.order;
        }
      });
    }
  }

  public processSection(inspection: Inspection, section: InspectionSection): InspectionSection {
    if (!('search' in section)) {
      if (inspection.sectionsById[section['id']]) {
        (section as InspectionSection)['order'] = inspection.sectionsById[section['id']].order;
      }
      section = _.merge(new InspectionSection(section['id'], section['name'], section['description']), section);
    }

    section.inspection = inspection;

    if (!inspection.sectionsById) {
      inspection.sectionsById = {};
    }

    inspection.sectionsById[section.id] = section;
    inspection.sections = inspection.sections.filter((s) => s.id !== section.id);
    inspection.sections.push(section);
    this.processFolders(inspection, section, section.folders);
    return section;
  }

  public updateSectionProperties(inspectionId: string, sectionId: string, properties: any) {
    const current: Inspection = this.inspectionsById[inspectionId];
    const section: InspectionSection = current.sectionsById[sectionId];
    section.name = properties.name;
    section.description = properties.description;
  }

  public processFolders(inspection: Inspection, section: InspectionSection, folders: InspectionFolder[]) {
    if (folders) {
      for (let i = 0; i < folders.length; i++) {
        const folder = folders[i];
        folders[i] = this.processFolder(inspection, section, folder, section);
      }
    }
  }

  public processFolder(inspection: Inspection, section: InspectionSection, folder: InspectionFolder, parent: any) {
    if (!('search' in folder)) {
      folder = _.merge(new InspectionFolder(folder['id'], folder['name'], folder['description']), folder);
    }
    folder.inspection = inspection;
    folder.section = inspection.sectionsById[section.id];
    folder.parent = new InspectionFolder(parent.id, parent.name, folder.description);
    folder.parent.etag = parent.etag;
    if (!inspection.foldersById) {
      inspection.foldersById = {};
    }
    inspection.foldersById[folder.id] = folder;
    if (folder.folders) {
      for (let i = 0; i < folder.folders.length; i++) {
        folder.folders[i] = this.processFolder(inspection, section, folder.folders[i], folder);
      }
    }
    return folder;
  }

  public getPath(folder: InspectionFolder) {}

  public processPoints(inspection: Inspection, points: InspectionPoint[]) {
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      if (!point.plugins) {
        point.plugins = [];
      }
      points[i] = this.processPoint(inspection, point);
    }
  }

  public processPlugins(inspection: Inspection, plugins: InspectionPlugin[]) {
    inspection.plugins = [];
    for (let i = 0; i < plugins.length; i++) {
      const plugin = plugins[i];
      plugins[i] = this.processPlugin(inspection, _.merge(new InspectionPlugin(plugin.id, plugin.order, plugin.pluginType), plugin));
    }
  }

  public processPluginsHasParent(inspection: Inspection, plugins: InspectionPlugin[]) {
    const pluginsHasParent = _.filter(plugins, function (p: InspectionPlugin) {
      return p.parentPluginId;
    });

    for (let i = 0; i < pluginsHasParent.length; i++) {
      const plugin = pluginsHasParent[i];
      const parent = inspection.pluginsById[plugin.parentPluginId];
      pluginsHasParent[i] = this.processPluginByParent(inspection, plugin, parent);
    }
  }

  public processPoint(inspection: Inspection, point: InspectionPoint): InspectionPoint {
    if (!('search' in point)) {
      point = _.merge(new InspectionPoint(point['id'], point['name'], point['description']), point);
    }

    point.inspection = inspection;

    point.section = inspection.sectionsById[point.sectionId];

    if (point.folderId && point.folderId !== 'undefined') {
      // NOTE : why are we creating a fake folder here. User will not be able to retrieve it in the interface
      point.folder = inspection.foldersById[point.folderId];
      if (!point.folder) {
        point.folder = new InspectionFolder('', '', '');
      }
      if (!point.folder.points) {
        point.folder.points = [];
      }
      const i = _.findIndex(point.folder.points, ['id', point.id]);
      if (i === -1) {
        point.folder.points.push(point);
      } else {
        point.folder.points[i] = point;
      }
    }
    if (point.section) {
      if (!point.section.points) {
        point.section.points = [];
      }

      const i = _.findIndex(point.section.points, ['id', point.id]);

      if (i === -1) {
        point.section.points.push(point);
      } else {
        point.section.points[i] = point;
      }
    }

    if (!inspection.pointsById) {
      inspection.pointsById = {};
    }
    inspection.pointsById[point.id] = point;
    inspection.points = inspection.points.filter((p) => p.id !== point.id);
    inspection.points.push(point);
    return point;
  }

  public processPlugin(inspection: Inspection, plugin: InspectionPlugin): InspectionPlugin {
    if ('search' in plugin) {
      plugin = _.merge(new InspectionPlugin(plugin.id, plugin.order, plugin.pluginType), plugin);
    }
    plugin.inspection = inspection;

    if (plugin.pointId && !plugin.parentPluginId) {
      plugin.point = inspection.pointsById[plugin.pointId];
      if (plugin.point) {
        if (!plugin.point.plugins) {
          plugin.point.plugins = [];
        }
        const i = _.findIndex(plugin.point.plugins, ['id', plugin.id]);
        if (i !== -1) {
          plugin.order = plugin.point.plugins[i].order;
          plugin.point.plugins[i] = plugin;
        } else {
          plugin.point.plugins.push(plugin);
        }
      }
    }
    if (!plugin.pointId && !plugin.parentPluginId) {
      if (!inspection.plugins) {
        inspection.plugins = [];
      }
      const i = _.findIndex(inspection.plugins, ['id', plugin.id]);
      if (i !== -1) {
        inspection.plugins[i] = plugin;
      } else {
        inspection.plugins.push(plugin);
      }
    }

    if (plugin.pluginType != null && plugin.pluginType.toLowerCase() === 'parts') {
      inspection.parts = plugin;
    }

    if (!inspection.pluginsById) {
      inspection.pluginsById = {};
    }

    inspection.pluginsById[plugin.id] = plugin;
    if (PluginComponent.initializers[plugin.pluginType]) {
      PluginComponent.initializers[plugin.pluginType](plugin);
    }

    return plugin;
  }

  public processPluginByParent(inspection: Inspection, plugin: InspectionPlugin, parent: InspectionPlugin) {
    if ('search' in plugin) {
      plugin = _.merge(new InspectionPlugin(plugin.id, plugin.order, plugin.pluginType), plugin);
    }

    if (plugin.pointId) {
      plugin.point = inspection.pointsById[plugin.pointId];
    }
    if (plugin.parentPluginId) {
      parent.plugins.push(plugin);
    }
    return plugin;
  }

  public removeInspection(id: string) {
    const inspection: Inspection = this.inspectionsById[id];
    if (inspection.parentId) {
      const parent: Inspection = this.inspectionsById[inspection.parentId];
      if (parent && parent.children) {
        parent.children = parent.children.filter((c) => c.id !== id);
      }
    }
    delete this.inspectionsById[id];
  }

  public removePlugin(id: string, pluginId: string) {
    const current: Inspection = this.inspectionsById[id];
    const plugin = current.pluginsById[pluginId];
    const point = current.points.find((p) => p.id === plugin?.pointId);
    _.remove(current.plugins, ['id', pluginId]);
    if (point) {
      _.remove(point.plugins, ['id', pluginId]);
    }
    delete current.pluginsById[pluginId];
  }

  public removeSection(id: string, sectionIds: string[]) {
    sectionIds.forEach((sectionId) => {
      const current: Inspection = this.inspectionsById[id];
      this.removeParts(
        id,
        current.sectionsById[sectionId].points.map((p) => p.id)
      );
      _.remove(current.sections, ['id', sectionId]);
      delete current.sectionsById[sectionId];
    });
  }

  public removePoint(id: string, pointId: string) {
    const current: Inspection = this.inspectionsById[id];
    if (current) {
      const point = current.pointsById[pointId];
      if (point) {
        const section = current.sections.find((s) => s.id === point.sectionId);
        const folderId = point.folder ? point.folder.id : null;
        if (folderId) {
          _.remove(current.foldersById[folderId].points, ['id', pointId]);
        }
        _.remove(current.points, ['id', pointId]);
        if (section) {
          _.remove(section.points, ['id', pointId]);
        }
        delete current.pointsById[pointId];
      }
    }
  }

  public removeFolder(id: string, sectionId: string, folderIds: string[]) {
    const current: Inspection = this.inspectionsById[id];
    folderIds.forEach((folderId) => {
      const folder = current.foldersById[folderId];
      const section = current.sections.find((s) => s.id === sectionId);
      _.remove(section.folders, ['id', folderId]);
      _.remove(current.sectionsById[sectionId].folders, ['id', folderId]);
      delete current.foldersById[folderId];
    });
  }

  public updateCustomer(inspectionId: string, customer: any) {
    const current: Inspection = this.inspectionsById[inspectionId];
    current.customerName = customer != null ? customer.Name : '';
    current.insptectionInspectionCustomer = customer;
  }

  public updateInspectedData(inspectionId: string, data: any) {
    const current: Inspection = this.inspectionsById[inspectionId];
    current.data = data;
  }

  public updateInspectionMachine(inspectionId: string, machine: any) {
    const current: Inspection = this.inspectionsById[inspectionId];
    if (!current.data) {
      current.data = {};
    }

    current.data.machine = machine;
  }

  public updateInspectionContact(inspectionId: string, contact: any) {
    const current: Inspection = this.inspectionsById[inspectionId];
    if (!current.data) {
      current.data = {};
    }
    current.technicienId = contact.TechnicienId;
    current.data.contact = contact;
  }

  public updateInspectionDate(inspectionId: string, events: any) {
    const current: Inspection = this.inspectionsById[inspectionId];
    current.startDate = events.StartDate;
  }

  public updateCategories(inspectionId: string, categories: any) {
    const current: Inspection = this.inspectionsById[inspectionId];
    current.categories = categories;
  }

  public updateStatus(inspectionId: string, status: number) {
    const current: Inspection = this.inspectionsById[inspectionId];
    current.status = status;
  }

  public updateInspectionProperties(inspection: Inspection) {
    const current: Inspection = this.inspectionsById[inspection.id];
    current.name = inspection.name;
    current.description = inspection.description;
    current.status = inspection.status;
    current.quotationNumber = inspection.quotationNumber;
    current.category = inspection.category;
    current.currency = inspection.currency;
    current.hourlyRate = inspection.hourlyRate;
    current.coverImage = inspection.coverImage;
  }

  public updateInspectionQuotation(inspection: Inspection) {
    const current: Inspection = this.inspectionsById[inspection.id];
    current.quotationNumber = inspection.quotationNumber;
  }

  public updateInspectionCurrency(id: string, currency: string) {
    const current: Inspection = this.inspectionsById[id];
    if (!current.currency) current.currency = currency;
  }

  public updateSectionsOrder(inspection: Inspection) {
    const current: Inspection = this.inspectionsById[inspection.id];
    current.sections.forEach((si) => {
      const section = inspection.sectionsItems.find((s) => s.id === si.id);
      if (section) {
        si.order = section.order;
      }
    });
  }

  public processIcons(toAdd: boolean, icon: Icon, inspectionId: string, pointId: string) {
    const inspection: Inspection = this.inspectionsById[inspectionId];
    const point: InspectionPoint = inspection.pointsById[pointId];
    Icons.processIcons(toAdd, icon, inspection, point);
  }

  updateInspectionSectionsOrder(id: string, orderableItems: any) {
    const current: Inspection = this.inspectionsById[id];
    _.each(orderableItems, (item: any) => {
      const section = current.sectionsById[item.id];
      if (section) {
        section.order = item.order;
      }
    });
  }

  updateInspectionPluginsOrder(id: string, orderableItems: any) {
    const current: Inspection = this.inspectionsById[id];
    current.plugins.forEach((p) => {
      p.order = orderableItems.find((o) => o.id === p.id).order;
    });
  }

  public setActionPlanId(inspectionId: string, actionPlanId: string) {
    const inspection: Inspection = this.inspectionsById[inspectionId];
    if (inspection) {
      inspection.actionPlanId = actionPlanId;
    }
  }

  public updateChildren(parentId: string, childrenIds: string[]) {
    const inspection: Inspection = this.inspectionsById[parentId];
    inspection.children = [];
    childrenIds.forEach((c) => {
      const child = this.inspectionsById[c];
      child.parentId = parentId;
      inspection.children.push(child);
    });
  }

  public deleteChildren(parentId: string, childrenIds: string[]) {
    const inspection: Inspection = this.inspectionsById[parentId];
    inspection.children = inspection.children.filter((c) => !childrenIds.includes(c.id));
    childrenIds.forEach((c) => {
      const child = this.inspectionsById[c];
      child.parentId = null;
    });
  }

  public setChangedPoint(pointId: string, flag = true) {
    this.changedPoints[pointId] = flag;
  }

  public isPointChanged(pointId: string) {
    return this.changedPoints[pointId];
  }

  public changeOffline(inspectionId: string, res: any) {
    const inspection: Inspection = this.inspectionsById[inspectionId];
    if (inspection) {
      Object.assign(inspection, res);
      inspection.updatedOffline = false;
      if (!res.isOffline) {
        inspection.offlinePersonId = null;
        inspection.offlineDate = null;
      }
    }
  }

  public updateInspectionOffline(inspectionId: string, value: boolean) {
    const inspection: Inspection = this.inspectionsById[inspectionId];
    if (inspection) {
      inspection.updatedOffline = value;
    }
  }

  public updatePluginUrl(inspectionId: string, plugins: InspectionPlugin[]) {
    const inspection: Inspection = this.inspectionsById[inspectionId];
    plugins.forEach((plugin) => {
      const pl = inspection.pluginsById[plugin.id];
      if (pl) {
        pl.postServerUrl = plugin.postServerUrl;
      }
    });
  }

  public processRestoreItems(inspectionId: string, items: InspectionRestoreItem[]): InspectionRestoreItem[] {
    const restoreItems: InspectionRestoreItem[] = [];
    const current: Inspection = this.inspectionsById[inspectionId];
    items.forEach((item) => {
      let location = '';
      const newItem = Object.assign(new InspectionRestoreItem(), item);
      if (newItem.sectionId) {
        const section: InspectionSection = current.sectionsById[newItem.sectionId];
        if (section) {
          location = section.name;
          if (newItem.folderId) {
            const folder: InspectionFolder = current.foldersById[newItem.folderId];
            if (folder) {
              location = `${location}/${folder.name}`;
            }
          }
        }
        newItem.location = location;
        restoreItems.push(newItem);
        return;
      }
      if (newItem.pointId) {
        const point: InspectionPoint = current.pointsById[newItem.pointId];
        if (point) {
          location = `${point.section.name}/${point.name}`;
          if (point.folderId) {
            const folder: InspectionFolder = current.foldersById[point.folderId];
            if (folder) {
              location = `${point.section.name}/${folder.name}/${point.name}`;
            }
          }
        }
        newItem.location = location;
        restoreItems.push(newItem);
        return;
      }
      restoreItems.push(newItem);
    });
    return restoreItems;
  }

  processSessions(inspectionId: string, sessions: InspectionSession[]) {
    const inspection = this.inspectionsById[inspectionId] as Inspection;
    inspection.sessions = sessions;
    for (const section of Object.values(inspection.sectionsById) as InspectionSection[]) {
      section.sessions = [];
    }
    for (const point of Object.values(inspection.pointsById) as InspectionPoint[]) {
      point.sessions = [];
    }
    for (const plugin of Object.values(inspection.pluginsById) as InspectionPlugin[]) {
      plugin.sessions = [];
    }
    for (const folder of Object.values(inspection.foldersById) as InspectionPlugin[]) {
      folder.sessions = [];
    }
    sessions.forEach((session) => {
      switch (session.entityType) {
        case EntityType.Point:
          const point = inspection.pointsById[session.entityId] as InspectionPoint;
          point.sessions.push(session);
          point.folder?.sessions.push(session);
          point.section.sessions.push(session);
          break;
        case EntityType.Folder:
          const folder = inspection.foldersById[session.entityId] as InspectionFolder;
          folder.sessions.push(session);
          folder.section.sessions.push(session);
          break;
        case EntityType.Section:
          const section = inspection.sectionsById[session.entityId] as InspectionSection;
          section.sessions.push(session);
          break;
        case EntityType.Plugin:
          const plugin = inspection.pluginsById[session.entityId] as InspectionPlugin;
          plugin.sessions.push(session);
          if (plugin.point) {
            plugin.point.sessions.push(session);
            plugin.point.section.sessions.push(session);
          }
          break;
      }
    });
  }
}
